import { buildPublicAssetUrl, getSiteHomePath } from "./appPath";

export const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000;

const UPDATE_INFO_TIMEOUT_MS = 1500;
const UPDATE_ACTIVATION_TIMEOUT_MS = 15000;


let registrationPromise = null;
let currentUpdate = null;
let lastUpdateCheckAt = 0;
let lifecycleListenersInstalled = false;
let controllerLifecycleInstalled = false;
let knownController = null;
let reloadScheduled = false;

const activationListeners = new Set();
const updateListeners = new Set();
const watchedRegistrations = new WeakSet();

const emitUpdate = (update) => {
  currentUpdate = update;

  updateListeners.forEach((listener) => {
    try {
      listener(update);
    } catch (error) {
      console.error("CheKhabare update listener failed:", error);
    }
  });
};


const emitActivation = () => {
  activationListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("CheKhabare activation listener failed:", error);
    }
  });
};

const installControllerLifecycle = () => {
  if (
    controllerLifecycleInstalled ||
    typeof window === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }

  controllerLifecycleInstalled = true;
  knownController = navigator.serviceWorker.controller || null;

  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      const nextController =
        navigator.serviceWorker.controller || null;

      /*
       * First install: the page had no controller before clients.claim().
       * Do not reload just because the first Service Worker took control.
       */
      if (!knownController) {
        knownController = nextController;
        return;
      }

      if (!nextController || nextController === knownController) {
        return;
      }

      knownController = nextController;
      currentUpdate = null;
      emitActivation();

      /*
       * The update may have been triggered in another open tab. Every
       * already-controlled CheKhabare tab moves to the same frontend build
       * after the user's single Update action, preventing mixed-version tabs.
       */
      if (!reloadScheduled) {
        reloadScheduled = true;
        window.location.reload();
      }
    }
  );
};

const requestWorkerInfo = (worker) => {
  if (!worker || typeof MessageChannel === "undefined") {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      channel.port1.onmessage = null;
      channel.port1.close?.();
      resolve(value);
    };

    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, UPDATE_INFO_TIMEOUT_MS);

    channel.port1.onmessage = (event) => {
      const payload = event.data;

      if (payload?.type === "CHEKHABARE_SW_INFO") {
        finish(payload);
      }
    };

    try {
      worker.postMessage(
        { type: "CHEKHABARE_SW_INFO" },
        [channel.port2]
      );
    } catch {
      finish(null);
    }
  });
};

const announceWaitingWorker = async (registration, worker) => {
  const waitingWorker = worker || registration?.waiting;

  if (
    !registration ||
    !waitingWorker ||
    !navigator.serviceWorker.controller
  ) {
    return;
  }

  if (currentUpdate?.worker === waitingWorker) {
    return;
  }

  const workerInfo = await requestWorkerInfo(waitingWorker);

  emitUpdate({
    registration,
    worker: waitingWorker,
    version: workerInfo?.appBuildVersion || null,
  });
};

const watchInstallingWorker = (registration, worker) => {
  if (!worker) return;

  const handleStateChange = () => {
    if (
      worker.state === "installed" &&
      navigator.serviceWorker.controller
    ) {
      announceWaitingWorker(
        registration,
        registration.waiting || worker
      );
    }
  };

  worker.addEventListener("statechange", handleStateChange);
  handleStateChange();
};

const installRegistrationWatchers = (registration) => {
  if (!registration || watchedRegistrations.has(registration)) {
    return;
  }

  watchedRegistrations.add(registration);

  if (registration.waiting && navigator.serviceWorker.controller) {
    announceWaitingWorker(registration, registration.waiting);
  }

  if (registration.installing) {
    watchInstallingWorker(registration, registration.installing);
  }

  registration.addEventListener("updatefound", () => {
    watchInstallingWorker(registration, registration.installing);
  });
};

const checkForAppUpdate = async (registration, force = false) => {
  if (!registration) return;

  const now = Date.now();

  if (
    !force &&
    now - lastUpdateCheckAt < UPDATE_CHECK_INTERVAL_MS
  ) {
    return;
  }

  lastUpdateCheckAt = now;

  try {
    await registration.update();

    if (registration.waiting && navigator.serviceWorker.controller) {
      await announceWaitingWorker(
        registration,
        registration.waiting
      );
    }
  } catch {
    // Offline/update-check failures must never interrupt the application.
  }
};

const installForegroundUpdateChecks = (registration) => {
  if (
    lifecycleListenersInstalled ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  lifecycleListenersInstalled = true;

  const checkWhenForegrounded = () => {
    if (document.visibilityState === "visible") {
      checkForAppUpdate(registration);
    }
  };

  document.addEventListener(
    "visibilitychange",
    checkWhenForegrounded
  );

  window.addEventListener("focus", checkWhenForegrounded);
  window.addEventListener("pageshow", checkWhenForegrounded);
};

/**
 * Registers CheKhabare's single application service worker.
 *
 * Phase 3 adds update detection on top of the Phase 2 App Shell cache. A newly
 * installed worker is intentionally left in the waiting state; it is activated
 * only after the user presses the single Update button in the top banner.
 */
export const registerAppServiceWorker = () => {
  if (
    process.env.NODE_ENV !== "production" ||
    typeof window === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return Promise.resolve(null);
  }

  if (registrationPromise) {
    return registrationPromise;
  }

  const workerUrl = buildPublicAssetUrl("service-worker.js");
  const workerScope = getSiteHomePath();

  registrationPromise = navigator.serviceWorker
    .register(workerUrl, {
      scope: workerScope,
      updateViaCache: "none",
    })
    .then((registration) => {
      installControllerLifecycle();
      installRegistrationWatchers(registration);
      installForegroundUpdateChecks(registration);

      /*
       * Explicitly check once on launch. Later foreground/focus checks are
       * throttled, so a long-running news session does not repeatedly hit the
       * service-worker URL.
       */
      checkForAppUpdate(registration, true);

      if (registration.waiting && navigator.serviceWorker.controller) {
        announceWaitingWorker(
          registration,
          registration.waiting
        );
      }

      return registration;
    })
    .catch((error) => {
      console.error(
        "CheKhabare service-worker registration failed:",
        error
      );

      return null;
    });

  return registrationPromise;
};

/**
 * Subscribe to the single pending frontend update, if one exists. The callback
 * also receives the already-detected update immediately when subscribing late.
 */
export const subscribeToAppUpdate = (listener) => {
  if (typeof listener !== "function") {
    return () => {};
  }

  updateListeners.add(listener);

  if (currentUpdate) {
    listener(currentUpdate);
  }

  return () => {
    updateListeners.delete(listener);
  };
};

/**
 * Activates the currently waiting worker only after an explicit user action.
 * Once that worker becomes the page controller, the app reloads exactly once
 * so the new version's cached App Shell is rendered as one consistent build.
 */
export const applyAppUpdate = async (update = currentUpdate) => {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return false;
  }

  const registration =
    update?.registration ||
    (registrationPromise ? await registrationPromise : null);

  const waitingWorker =
    update?.worker || registration?.waiting || null;

  if (!waitingWorker) {
    return false;
  }

  installControllerLifecycle();

  return new Promise((resolve) => {
    let settled = false;

    const finish = (didActivate) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      activationListeners.delete(handleActivation);
      resolve(didActivate);
    };

    const handleActivation = () => {
      finish(true);
    };

    const timeoutId = window.setTimeout(() => {
      finish(false);
    }, UPDATE_ACTIVATION_TIMEOUT_MS);

    activationListeners.add(handleActivation);

    try {
      waitingWorker.postMessage({
        type: "CHEKHABARE_SKIP_WAITING",
      });
    } catch {
      finish(false);
    }
  });
};

/**
 * Reuses the same registration promise so no second service-worker
 * registration is created anywhere in the application.
 */
export const getAppServiceWorkerRegistration = () => {
  return registrationPromise;
};

export const getPendingAppUpdate = () => currentUpdate;

export const checkAppUpdateNow = async () => {
  const registration = registrationPromise
    ? await registrationPromise
    : await registerAppServiceWorker();

  await checkForAppUpdate(registration, true);
};
